import { useMemo } from 'react';
import { useAuthStore, type ModuleAccess, type User } from '../store/authStore';

export type PermissionAction = 'view' | 'create' | 'update' | 'delete' | 'export';

/** A super admin is never restricted by the matrix (mirrors the backend PermissionGuard bypass). */
function isSuperAdmin(user: User | null): boolean {
  if (!user || user.userType !== 'ADMIN') return false;
  const marker = String(user.adminType ?? user.role ?? '').trim().toUpperCase();
  return marker === 'SUPER_ADMIN';
}

function flagForAction(access: ModuleAccess, action: PermissionAction): boolean {
  switch (action) {
    case 'view':
      return !!access.canView;
    case 'create':
      return !!access.canCreate;
    case 'update':
      return !!access.canUpdate;
    case 'delete':
      return !!access.canDelete;
    case 'export':
      return !!access.canExport;
    default:
      return false;
  }
}

/**
 * Resolve whether the current user may perform `action` on `moduleCode`, honouring the same policy
 * the backend enforces:
 *  - SUPER_ADMIN: always allowed.
 *  - Empty matrix (no moduleAccesses at all): allowed — a never-configured user keeps legacy access,
 *    so turning gating on can't lock anyone out until a matrix exists for them.
 *  - Otherwise: the module must be present and its action flag true.
 *
 * Use `can(moduleCode, action)` for buttons/actions, and `canView(moduleCode)` for page/section gating.
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);

  return useMemo(() => {
    const superAdmin = isSuperAdmin(user);
    const accesses = user?.moduleAccesses ?? [];
    const configured = accesses.length > 0;
    const byCode = new Map(accesses.map((a) => [a.moduleCode, a] as const));

    const can = (moduleCode: string, action: PermissionAction = 'view'): boolean => {
      if (superAdmin) return true;
      if (!configured) return true; // anti-lockout: unconfigured user keeps legacy access
      const access = byCode.get(moduleCode);
      if (!access) return false;
      return flagForAction(access, action);
    };

    return {
      can,
      canView: (moduleCode: string) => can(moduleCode, 'view'),
      canCreate: (moduleCode: string) => can(moduleCode, 'create'),
      canUpdate: (moduleCode: string) => can(moduleCode, 'update'),
      canDelete: (moduleCode: string) => can(moduleCode, 'delete'),
      canExport: (moduleCode: string) => can(moduleCode, 'export'),
      isSuperAdmin: superAdmin,
      /** True once a matrix exists for this user (i.e. gating is in effect for them). */
      isConfigured: configured,
    };
  }, [user]);
}
